import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Calendar, MapPin, Video, Phone, Users,
  CheckCircle, XCircle, Clock, MessageSquare
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TalentInterviews = () => {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInterviews();
  }, []);

  const fetchInterviews = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/talent/me/interviews`);
      setInterviews(response.data.interviews || []);
    } catch (error) {
      toast.error('Failed to load interviews');
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (interviewId, response, selectedTime = null) => {
    try {
      await axios.patch(`${API}/talent/interviews/${interviewId}/respond`, { 
        response,
        selected_time: selectedTime
      });
      toast.success(`Interview ${response}`);
      fetchInterviews();
    } catch (error) {
      toast.error('Failed to respond to interview');
    }
  };

  const getInterviewIcon = (type) => {
    const icons = {
      phone: <Phone className="w-5 h-5" />,
      video_call: <Video className="w-5 h-5" />,
      in_person: <Users className="w-5 h-5" />
    };
    return icons[type] || <Calendar className="w-5 h-5" />;
  };

  const getInterviewLabel = (type) => {
    const labels = {
      phone: 'Phone Call',
      video_call: 'Video Call',
      in_person: 'In-Person'
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const badges = {
      sent: <Badge className="bg-blue-100 text-blue-800">Sent</Badge>,
      viewed: <Badge className="bg-yellow-100 text-yellow-800">Viewed</Badge>,
      accepted: <Badge className="bg-green-100 text-green-800">Accepted</Badge>,
      declined: <Badge className="bg-red-100 text-red-800">Declined</Badge>,
      rescheduled: <Badge className="bg-purple-100 text-purple-800">Rescheduled</Badge>,
      completed: <Badge className="bg-gray-100 text-gray-800">Completed</Badge>,
      cancelled: <Badge className="bg-gray-100 text-gray-800">Cancelled</Badge>
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Interview Invitations</h1>
          <p className="text-muted-foreground">Manage interview requests from employers</p>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading interviews...</div>
        ) : interviews.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-xl font-semibold mb-2">No Interview Invites</h3>
              <p className="text-muted-foreground">
                When employers invite you to interview, they'll appear here
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {interviews.map((interview) => {
              const canRespond = interview.status === 'sent' || interview.status === 'viewed';

              return (
                <Card key={interview.id} className="border-2 border-border">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          {getInterviewIcon(interview.interview_type)}
                          <h3 className="text-xl font-bold text-foreground">
                            {getInterviewLabel(interview.interview_type)}
                          </h3>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          From: {interview.company?.company_name || 'Company'}
                        </div>
                      </div>
                      {getStatusBadge(interview.status)}
                    </div>

                    {/* Proposed Times */}
                    {interview.proposed_times && interview.proposed_times.length > 0 && (
                      <div className="mb-4">
                        <div className="text-sm font-medium text-foreground mb-2">Proposed Times:</div>
                        <div className="space-y-2">
                          {interview.proposed_times.map((time, index) => (
                            <div key={index} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                              <Calendar className="w-4 h-4 text-purple-600" />
                              <span className="text-sm font-medium">
                                {new Date(time).toLocaleString('en-AU', {
                                  weekday: 'short',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                              {canRespond && (
                                <button
                                  onClick={() => handleRespond(interview.id, 'accepted', time)}
                                  className="ml-auto px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                                >
                                  Accept This Time
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Location or Link */}
                    {interview.location_or_link && (
                      <div className="mb-4">
                        <div className="text-sm font-medium text-foreground mb-1">
                          {interview.interview_type === 'in_person' ? 'Location:' : 'Meeting Link:'}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {interview.location_or_link}
                        </div>
                      </div>
                    )}

                    {/* Message */}
                    {interview.message && (
                      <div className="bg-muted/50 rounded-lg p-4 mb-4">
                        <div className="text-sm font-medium text-foreground mb-1">Message:</div>
                        <p className="text-sm text-muted-foreground whitespace-pre-line">{interview.message}</p>
                      </div>
                    )}

                    {canRespond && (
                      <div className="flex gap-2 pt-4 border-t border-border">
                        <button
                          onClick={() => handleRespond(interview.id, 'declined')}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-muted text-sm font-medium"
                        >
                          <XCircle className="w-4 h-4" />
                          Decline
                        </button>
                        <button
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-muted text-sm font-medium"
                        >
                          <MessageSquare className="w-4 h-4" />
                          Suggest Alternative
                        </button>
                      </div>
                    )}

                    <div className="text-xs text-muted-foreground mt-2">
                      Received {new Date(interview.created_at).toLocaleDateString()}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TalentInterviews;
